import { useEffect, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'

import CustomPagination from '@/shared/components/slider/components/Pagination'
import type { SliderOptions } from '@/shared/components/slider/types/sliderOptions'
import type { SlideItem } from '@/shared/components/slider/view'
import type { CSSProperties } from 'react'
import type { Swiper as SwiperCore } from 'swiper/types'

import 'swiper/css'
import 'swiper/css/free-mode'

interface SliderProps {
  currentSlide: number
  setCurrentSlide: React.Dispatch<React.SetStateAction<number>>
  onClick?: () => void
  options: SliderOptions
  slideComponent: ({
    item,
    isNotNaverInApp,
    setCurrentSlide,
    imgIndex
  }: {
    item: SlideItem
    isNotNaverInApp?: boolean
    setCurrentSlide: React.Dispatch<React.SetStateAction<number>>
    imgIndex: number
  }) => JSX.Element
  isNotNaverInApp?: boolean
  items: SlideItem[]
}

const SliderCore = ({
  currentSlide,
  setCurrentSlide,
  options,
  onClick,
  items,
  slideComponent: Slide,
  isNotNaverInApp
}: SliderProps) => {
  const swiperRef = useRef<SwiperCore | null>(null)

  const { pagination = false, sliderStyles, paginationStyles, isFreeMode = false, swiperModules = [] } = options

  const defaultSliderStyles = 'relative w-full max-w-[768px] overflow-hidden'

  const freeModeStyle: { swiper: CSSProperties; slide: CSSProperties } = {
    swiper: {
      paddingLeft: isFreeMode ? '16px' : undefined,
      paddingRight: isFreeMode ? '16px' : undefined
    },
    slide: {
      width: isFreeMode ? 'auto' : undefined
    }
  }

  const handleSlideChange = (swiperContext: SwiperCore) => {
    if (isFreeMode) return

    setCurrentSlide(swiperContext.realIndex)
  }

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(currentSlide)
    }
  }, [currentSlide])

  return (
    <Swiper
      modules={swiperModules}
      freeMode={isFreeMode}
      slidesPerView="auto"
      spaceBetween={isFreeMode ? 10 : 0}
      className={sliderStyles ? defaultSliderStyles + ' ' + sliderStyles : defaultSliderStyles}
      onSlideChange={handleSlideChange}
      initialSlide={isFreeMode ? 0 : currentSlide}
      onSwiper={(swiper) => {
        swiperRef.current = swiper
      }}
      style={{ zIndex: 0, ...freeModeStyle.swiper }}
    >
      {items.map((item, i) => (
        <SwiperSlide key={`slide-${i}`} onClick={onClick} style={freeModeStyle.slide}>
          <Slide isNotNaverInApp={isNotNaverInApp} item={item} imgIndex={i} setCurrentSlide={setCurrentSlide} />
        </SwiperSlide>
      ))}
      {pagination && <CustomPagination currentSlide={currentSlide} total={items.length} styles={paginationStyles} />}
    </Swiper>
  )
}

export default SliderCore
